import i18n from "@/i18n";
/** WORKFORCE-EXTERNAL-EXTRAS-QR-V1 — approve / reject / correct one report. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { formatYMDDate } from "./workforceHelpers";
import { EXTRA_STATUS_LABEL, extraErrorMessage, formatMinutes } from "./externalExtrasHelpers";

const yn = (v) => (v ? i18n.t("common.yes", "Yes") : i18n.t("common.no", "No"));

function Facts({ c, testid }) {
    return (
        <p className="text-sm text-neutral-700" data-testid={testid}>
            {c.name} · {c.agency} · {formatYMDDate(c.shift_date)} {c.start_time}–{c.end_time}
            {c.end_date !== c.shift_date ? " (+1 day)" : ""} · {formatMinutes(c.duration_minutes)} ·
            meal {yn(c.staff_meal)} · finished as expected {yn(c.finished_as_expected)}
        </p>
    );
}

function History({ items }) {
    return (
        <ol className="space-y-1 text-xs text-neutral-600" data-testid="extras-review-history">
            {items.map((h) => (
                <li key={h.revision}>
                    #{h.revision} {h.action} {h.at?.slice(0, 16).replace("T", " ")}
                    {h.reason ? ` · ${h.reason}` : ""}
                    {h.after ? ` · ${Object.entries(h.after).map(([k, v]) => `${k}: ${h.before?.[k]} → ${v}`).join(", ")}` : ""}
                    {h.after_close ? " · after period close" : ""}
                </li>
            ))}
        </ol>
    );
}

export function ExternalExtrasReviewDialog({ report, restaurantId, onClose, onSaved, canManage = true }) {
    const [mode, setMode] = useState(null);
    const [reason, setReason] = useState("");
    const [edit, setEdit] = useState({});
    const [busy, setBusy] = useState(false);
    useEffect(() => { setMode(null); setReason(""); setEdit(report ? { ...report.current } : {}); }, [report]);
    if (!report) return null;
    const c = report.current;

    const send = async (action) => {
        const body = { restaurant_id: restaurantId, action, reason, expected_revision: report.revision };
        if (action === "CORRECT") {
            ["name", "agency", "shift_date", "start_time", "end_time", "staff_meal", "finished_as_expected"]
                .forEach((k) => { if (edit[k] !== c[k]) body[k] = edit[k]; });
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/workforce/external-extras/reports/${report.report_id}/review`, body);
            toast.success({ APPROVE: i18n.t("wf.externalExtrasReviewDialog.approved", "Approved"), REJECT: i18n.t("wf.externalExtrasReviewDialog.rejected", "Rejected"), CORRECT: i18n.t("wf.externalExtrasReviewDialog.correctionSaved", "Correction saved") }[action]);
            onSaved(data);
        } catch (e) {
            toast.error(extraErrorMessage(e, "Could not save."));
        } finally {
            setBusy(false);
        }
    };
    const field = (k, type = "text") => (
        <Input type={type} value={edit[k] ?? ""} data-testid={`extras-correct-${k}`}
            onChange={(e) => setEdit((x) => ({ ...x, [k]: e.target.value }))} />
    );
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="extras-review-dialog">
                <DialogHeader><DialogTitle>Extra shift report · {EXTRA_STATUS_LABEL[report.status]}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <Facts c={c} testid="extras-review-current" />
                    {report.revision > 1 && (
                        <div className="text-xs text-neutral-500">
                            Originally sent: <Facts c={report.original} testid="extras-review-original" />
                        </div>
                    )}
                    <History items={report.history || []} />
                    {mode === "CORRECT" && (
                        <div className="grid grid-cols-2 gap-2">
                            {field("name")}{field("agency")}{field("shift_date", "date")}
                            {field("start_time", "time")}{field("end_time", "time")}
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={Boolean(edit.staff_meal)} data-testid="extras-correct-meal"
                                    onChange={(e) => setEdit((x) => ({ ...x, staff_meal: e.target.checked }))} />Staff meal
                            </label>
                            <label className="col-span-2 flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={Boolean(edit.finished_as_expected)}
                                    data-testid="extras-correct-finished"
                                    onChange={(e) => setEdit((x) => ({ ...x, finished_as_expected: e.target.checked }))} />
                                {i18n.t("wf.externalExtrasReviewDialog.finishedAtTheExpectedTime", "Finished at the expected time")}
                            </label>
                        </div>
                    )}
                    {mode && (
                        <Input placeholder={i18n.t("wf.externalExtrasReviewDialog.reasonRequired", "Reason (required)")} value={reason} data-testid="extras-review-reason"
                            onChange={(e) => setReason(e.target.value)} />
                    )}
                    {canManage && <div className="flex flex-wrap gap-2">
                        {mode ? (
                            <>
                                <Button disabled={busy} data-testid="extras-review-save" onClick={() => send(mode)}>
                                    {mode === "REJECT" ? i18n.t("wf.externalExtrasReviewDialog.rejectReport", "Reject report") : i18n.t("wf.externalExtrasReviewDialog.saveCorrection", "Save correction")}
                                </Button>
                                <Button variant="ghost" onClick={() => setMode(null)}>{i18n.t("common.back", "Back")}</Button>
                            </>
                        ) : (
                            <>
                                {report.status !== "APPROVED" && (
                                    <Button disabled={busy} data-testid="extras-review-approve" onClick={() => send("APPROVE")}>
                                        {i18n.t("wf.externalExtrasReviewDialog.approve", "Approve")}
                                    </Button>
                                )}
                                {report.status !== "REJECTED" && (
                                    <>
                                        <Button variant="outline" data-testid="extras-review-correct"
                                            onClick={() => setMode("CORRECT")}>{i18n.t("wf.externalExtrasReviewDialog.correct", "Correct")}</Button>
                                        <Button variant="outline" data-testid="extras-review-reject"
                                            onClick={() => setMode("REJECT")}>{i18n.t("wf.externalExtrasReviewDialog.reject", "Reject")}</Button>
                                    </>
                                )}
                            </>
                        )}
                    </div>}
                    <p className="text-xs text-neutral-500">
                        {i18n.t("wf.externalExtrasReviewDialog.onlyApprovedReportsCountAsWorked", "Only approved reports count as worked hours in Reports and Teho. Meal and finish answers are kept as facts and do not change the hours or create deductions.")}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
